// src/services/chargilyService.js
import supabaseService from './supabaseService';

class ChargilyService {
  constructor() {
    // استخدام مفاتيح الاختبار المقدمة
    this.publicKey = 'test_pk_k3sSWJ5blfGeXkbq2o6BJVDJnCwlpNch9PcWooo4';
    this.secretKey = 'test_sk_FIOtUxOfCsguEhJ8PKEly9MYEuveUaqzWIO1khSB';
    this.baseURL = 'https://pay.chargily.net/test/api/v2'; // استخدام بيئة الاختبار
  }

  /**
   * إنشاء عملية دفع جديدة
   * @param {Object} paymentData - بيانات الدفع
   * @param {number} paymentData.amount - المبلغ بالدينار الجزائري (DZD)
   * @param {string} paymentData.description - وصف العملية
   * @param {string} paymentData.customerEmail - بريد العميل
   * @param {string} paymentData.customerName - اسم العميل
   * @param {string} paymentData.metadata - بيانات إضافية (provider_id, payment_type, etc.)
   * @returns {Promise<Object>} - بيانات الدفع (checkout_url, payment_id)
   */
  async createCheckout(paymentData) {
    try {
      console.log('💳 Creating Chargily checkout...', paymentData);

      // الحصول على معلومات المستخدم الحالي
      const currentUser = supabaseService.currentUser;
      if (!currentUser) {
        throw new Error('المستخدم غير مسجل الدخول');
      }

      // 🔧 تحديد الـ base URL (حل مشكلة localhost)
      const isLocalhost = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1' ||
                         window.location.hostname.includes('192.168');
      
      // في حالة localhost، استخدم رابط وهمي صالح
      const baseUrl = isLocalhost 
        ? 'https://example.com' 
        : window.location.origin;

      console.log('🌐 Using base URL:', baseUrl, '(localhost:', isLocalhost, ')');

      // تحضير البيانات للإرسال
      const checkoutData = {
        amount: Math.round(paymentData.amount * 100), // تحويل إلى سنتيم (أصغر وحدة)
        currency: 'dzd',
        success_url: `${baseUrl}/payment-success?session_id={CHECKOUT_ID}`,
        failure_url: `${baseUrl}/payment-failed`,
        webhook_endpoint: isLocalhost ? undefined : `${baseUrl}/.netlify/functions/chargily-webhook`, // تعطيل webhook في localhost
        description: paymentData.description || 'دفع شهري - خدمة',
        locale: 'ar',
        pass_fees_to_customer: false, // المزود يدفع الرسوم
        metadata: {
          user_id: currentUser.id,
          user_email: currentUser.email,
          is_localhost: isLocalhost, // علامة للتعرف على العمليات المحلية
          ...paymentData.metadata
        }
      };

      // إضافة معلومات العميل إذا كانت متوفرة
      if (paymentData.customerEmail) {
        checkoutData.customer = {
          email: paymentData.customerEmail,
          name: paymentData.customerName || 'عميل'
        };
      }

      console.log('📤 Sending checkout request:', checkoutData);

      // إرسال الطلب لـ Chargily API
      const response = await fetch(`${this.baseURL}/checkouts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.secretKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(checkoutData)
      });

      const result = await response.json();

      if (!response.ok) {
        console.error('❌ Chargily API Error:', result);
        throw new Error(result.message || 'فشل إنشاء عملية الدفع');
      }

      console.log('✅ Checkout created successfully:', result);

      return {
        success: true,
        checkoutId: result.id,
        checkoutUrl: result.checkout_url,
        data: result
      };

    } catch (error) {
      console.error('❌ Error creating Chargily checkout:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * التحقق من حالة عملية الدفع
   * @param {string} checkoutId - معرف عملية الدفع
   * @returns {Promise<Object>} - حالة الدفع
   */
  async getCheckoutStatus(checkoutId) {
    try {
      console.log('🔍 Checking checkout status:', checkoutId);

      const response = await fetch(`${this.baseURL}/checkouts/${checkoutId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.secretKey}`,
          'Content-Type': 'application/json',
        }
      });

      const result = await response.json();

      if (!response.ok) {
        console.error('❌ Error fetching checkout:', result);
        throw new Error(result.message || 'فشل جلب حالة الدفع');
      }

      console.log('✅ Checkout status:', result);

      return {
        success: true,
        status: result.status, // paid, pending, failed, expired
        data: result
      };

    } catch (error) {
      console.error('❌ Error getting checkout status:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * استرجاع جميع عمليات الدفع
   * @param {number} page - رقم الصفحة
   * @param {number} perPage - عدد العناصر في الصفحة
   * @returns {Promise<Object>} - قائمة العمليات
   */
  async listCheckouts(page = 1, perPage = 10) {
    try {
      const response = await fetch(
        `${this.baseURL}/checkouts?page=${page}&per_page=${perPage}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${this.secretKey}`,
            'Content-Type': 'application/json',
          }
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'فشل جلب قائمة العمليات');
      }

      return {
        success: true,
        data: result.data,
        pagination: result.pagination
      };

    } catch (error) {
      console.error('❌ Error listing checkouts:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * التحقق من صحة Webhook Signature
   * @param {string} signature - التوقيع من الـ header
   * @param {Object} payload - البيانات المستلمة
   * @returns {boolean} - صحة التوقيع
   */
  verifyWebhookSignature(signature, payload) {
    try {
      // Chargily يستخدم HMAC SHA-256
      const crypto = require('crypto');
      const computedSignature = crypto
        .createHmac('sha256', this.secretKey)
        .update(JSON.stringify(payload))
        .digest('hex');

      return signature === computedSignature;
    } catch (error) {
      console.error('❌ Error verifying webhook signature:', error);
      return false;
    }
  }

  /**
   * معالجة إشعارات Webhook
   * @param {Object} webhookData - البيانات المستلمة من Chargily
   * @returns {Promise<boolean>} - نجاح المعالجة
   */
  async handleWebhook(webhookData) {
    try {
      console.log('🔔 Processing Chargily webhook:', webhookData);

      const { type, data } = webhookData;

      switch (type) {
        case 'checkout.paid':
          await this.handleSuccessfulPayment(data);
          break;
        
        case 'checkout.failed':
          await this.handleFailedPayment(data);
          break;

        case 'checkout.expired':
          console.log('⏰ Checkout expired:', data.id);
          break;

        default:
          console.log('ℹ️ Unhandled webhook type:', type);
      }

      return true;
    } catch (error) {
      console.error('❌ Error handling webhook:', error);
      return false;
    }
  }

  /**
   * معالجة الدفع الناجح
   * @param {Object} checkoutData - بيانات عملية الدفع
   */
  async handleSuccessfulPayment(checkoutData) {
    try {
      console.log('✅ Processing successful payment:', checkoutData);

      const { metadata } = checkoutData;

      if (!metadata) {
        console.warn('⚠️ No metadata found in checkout');
        return;
      }

      const { user_id, provider_id, payment_type } = metadata;

      // تحديث حالة الدفع في قاعدة البيانات
      if (payment_type === 'monthly' && provider_id) {
        // تحديث الدفع الشهري
        const now = new Date();
        const currentMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));

        await supabaseService.client
          .from('monthlypayments')
          .upsert({
            provider_id: provider_id,
            month: currentMonth.toISOString(),
            payment_status: 'paid',
            receipt_url: checkoutData.invoice_url || null,
            amount: checkoutData.amount / 100, // تحويل من سنتيم
            payment_method: 'chargily',
            transaction_id: checkoutData.id,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'provider_id,month'
          });

        console.log('✅ Monthly payment updated successfully');
      }

      // إرسال إشعار للمستخدم
      await supabaseService.createNotification({
        user_id: user_id || provider_id,
        title: 'تم الدفع بنجاح',
        message: `تمت عملية الدفع بنجاح بمبلغ ${checkoutData.amount / 100} دج`,
        is_read: false
      });

    } catch (error) {
      console.error('❌ Error handling successful payment:', error);
    }
  }

  /**
   * معالجة الدفع الفاشل
   * @param {Object} checkoutData - بيانات عملية الدفع
   */
  async handleFailedPayment(checkoutData) {
    try {
      console.log('❌ Processing failed payment:', checkoutData);

      const { metadata } = checkoutData;

      if (metadata?.user_id) {
        // إرسال إشعار للمستخدم
        await supabaseService.createNotification({
          user_id: metadata.user_id,
          title: 'فشل عملية الدفع',
          message: 'لم تتم عملية الدفع بنجاح. يرجى المحاولة مرة أخرى.',
          is_read: false
        });
      }

    } catch (error) {
      console.error('❌ Error handling failed payment:', error);
    }
  }
}

// إنشاء نسخة واحدة من الخدمة
const chargilyService = new ChargilyService();

export default chargilyService;